import { useCallback, useRef, useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import logger from "../utils/UnifiedLogger";
import { useAbortController } from "./useAbortController";
import {
  trackApiError,
  trackNetworkError,
  trackTimeoutError,
} from "../utils/ErrorTracker";
import { encryptForBackend } from "../utils/utilCrypto";
import { useSelector } from "react-redux";
import { selectSelectedChatId } from "../store/features/message/textContentSelectors";
import { API_ENDPOINTS } from "../utils/constants";
import { useDispatch } from "react-redux";
import {
  setInputDisabled,
  setIsVoiceMuteButtonVisible,
  setIsVoiceProcessing,
  setMicButtonDisabled,
  setSendButtonDisabled,
} from "../store/features/ui/uiSlice";
import { getChatConfig } from "../utils/chatConfigStorage";
// import {
//   startBackgroundTone,
//   stopBackgroundTone,
// } from "../utils/backgroundTonePlayer";

export const useApi = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedChatId = useSelector(selectSelectedChatId);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const requestQueue = useRef([]);
  const activeRequests = useRef(new Map());
  const retryAttempts = useRef(new Map());
  const botStatusPollers = useRef(new Map());
  const {
    createApiController,
    abortApiRequest,
    abortAllApiRequests,
    isGlobalAborted,
  } = useAbortController();

  // Bot status message types to rotate through
  const BOT_STATUS_TYPES = [
    "INITIATING",
    "PROCESSING",
    "WORKING",
    "PROGRESS",
    "DELAY",
    "OPTIMIZING",
    "FINALIZING",
    "PATIENCE",
  ];

  const sendLogToServer = useCallback(async (logData, xSource) => {
    axios
      .post(API_ENDPOINTS.frontend_timing_logger, logData, {
        timeout: 5000, // Short timeout for logging
        headers: {
          "Content-Type": "application/json",
          "x-source": xSource, // Mark this as a logging request
        },
      })
      .catch((err) => {
        // Silently fail - don't let logging errors affect the main app
        console.warn("Failed to send log to server:", err.message);
      });
  }, []);

  const getTimeoutMessages = useCallback(
    () => [
      {
        time: 2000,
        message: t("botResponseMessage.api.initiating"),
        type: "INITIATING",
      },
      {
        time: 5000,
        message: t("botResponseMessage.api.processingRequest"),
        type: "PROCESSING",
      },
      {
        time: 10000,
        message: t("botResponseMessage.api.stillWorking"),
        type: "WORKING",
      },
      {
        time: 15000,
        message: t("botResponseMessage.api.almostThere"),
        type: "PROGRESS",
      },
      {
        time: 25000,
        message: t("botResponseMessage.api.complexProcessing"),
        type: "DELAY",
      },
      {
        time: 40000,
        message: t("botResponseMessage.api.optimizingResults"),
        type: "OPTIMIZING",
      },
      {
        time: 60000,
        message: t("botResponseMessage.api.finalProcessing"),
        type: "FINALIZING",
      },
      {
        time: 90000,
        message: t("botResponseMessage.api.thankYouForPatience"),
        type: "PATIENCE",
      },
    ],
    [t]
  );

  // NEW: Bot Status Polling Function
  const startBotStatusPolling = useCallback(
    (requestId, botStatusConfig = {}) => {
      const {
        statusUrl = `${API_ENDPOINTS.progressUpdateOnGrievance}?lang=en`, // Your bot status endpoint
        pollInterval = 2000, // Poll every 2 seconds
        onStatusUpdate = () => {},
      } = botStatusConfig;

      let pollCount = 0;
      let isPolling = true;

      const getRandomType = () => {
        const randomIndex = Math.floor(Math.random() * BOT_STATUS_TYPES.length);
        return BOT_STATUS_TYPES[randomIndex];
      };

      const pollBotStatus = async () => {
        if (!isPolling) return;

        try {
          // ADD THIS: Safety check - stop after 5 minutes
          if (pollCount > 150) {
            // 150 polls * 2 seconds = 5 minutes
            // logger.warning(
            //   "BOT_STATUS",
            //   "Stopping polling due to max attempts",
            //   {
            //     requestId,
            //     pollCount,
            //   }
            // );
            isPolling = false;
            return;
          }

          const randomType = getRandomType();
          pollCount++;

          // FIX: Use botStatusConfig.headers instead
          const xSource =
            botStatusConfig.headers?.["x-source"] ||
            botStatusConfig.headers?.["X-Source"] ||
            selectedChatId?.replace(/-/g, "_") ||
            "unknown";

          const response = await axios.get(statusUrl, {
            headers: {
              "x-source": xSource,
            },
            timeout: 5000, // Short timeout for status checks
          });

          logger.debug("BOT_STATUS", "Polling bot status", {
            requestId,
            pollCount,
            type: randomType,
            statusData: response.data,
            xSource,
          });

          if (response.data) {
            onStatusUpdate({
              requestId,
              pollCount,
              type: randomType,
              statusData: response.data,
              timestamp: Date.now(),
            });
          }

          // Schedule next poll
          if (isPolling) {
            const timeoutId = setTimeout(pollBotStatus, pollInterval);

            // Store the timeout ID so we can clear it later
            const poller = botStatusPollers.current.get(requestId);
            if (poller) {
              poller.timeoutId = timeoutId;
            }
          }
        } catch (error) {
          // logger.warning("BOT_STATUS", "Failed to poll bot status", {
          //   requestId,
          //   pollCount,
          //   error: error.message,
          // });

          // Continue polling even on error
          if (isPolling) {
            const timeoutId = setTimeout(pollBotStatus, pollInterval);

            const poller = botStatusPollers.current.get(requestId);
            if (poller) {
              poller.timeoutId = timeoutId;
            }
          }
        }
      };

      // Store poller reference
      botStatusPollers.current.set(requestId, {
        isPolling: true,
        pollCount: 0,
        startTime: Date.now(),
        timeoutId: null,
        stop: () => {
          isPolling = false;
          const poller = botStatusPollers.current.get(requestId);
          if (poller?.timeoutId) {
            clearTimeout(poller.timeoutId);
          }
          botStatusPollers.current.delete(requestId);

          // logger.info("BOT_STATUS", "Stopped polling", {
          //   requestId,
          //   totalPolls: pollCount,
          //   duration:
          //     Date.now() - botStatusPollers.current.get(requestId)?.startTime,
          // });
        },
      });

      // Start polling
      pollBotStatus();

      return botStatusPollers.current.get(requestId);
    },
    [BOT_STATUS_TYPES, selectedChatId]
  );

  // NEW: Stop Bot Status Polling
  const stopBotStatusPolling = useCallback((requestId) => {
    const poller = botStatusPollers.current.get(requestId);
    if (poller) {
      poller.stop();
    }
  }, []);

  const calculateRequestPriority = useCallback((config) => {
    const {
      url,
      method,
      priority = "normal",
      // component,
      // params,
      // body,
      // query,
    } = config;
    let score = 50;

    if (method === "GET") score += 10;
    if (method === "POST") score += 20;
    if (method === "PUT" || method === "PATCH") score += 15;
    if (method === "DELETE") score += 25;

    if (url.includes("/auth/")) score += 30;
    if (url.includes("/user/")) score += 20;
    if (url.includes("/search/")) score += 15;
    if (url.includes("/analytics/")) score -= 10;

    const priorityMap = { low: -20, normal: 0, high: 20, critical: 40 };
    score += priorityMap[priority] || 0;

    return Math.max(0, Math.min(100, score));
  }, []);

  const shouldRetry = useCallback((error, attempt, config) => {
    const maxRetries = config.maxRetries || 3;
    if (attempt >= maxRetries) return false;

    if (error.response?.status >= 400 && error.response?.status < 500) {
      return [408, 429, 499].includes(error.response.status);
    }

    return (
      error.code === "ECONNABORTED" ||
      error.code === "NETWORK_ERROR" ||
      error.response?.status >= 500
    );
  }, []);

  const generateCurlCommand = useCallback(
    (config, body, params = {}, query = {}) => {
      try {
        const queryString = new URLSearchParams(query).toString();
        const fullUrl = queryString
          ? `${config.url}?${queryString}`
          : config.url;

        let curl = `curl -X ${config.method.toUpperCase()}`;
        curl += ` '${fullUrl}'`;

        if (config.headers && typeof config.headers === "object") {
          Object.entries(config.headers).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              curl += ` -H '${key}: ${String(value)}'`;
            }
          });
        }

        if (
          params &&
          typeof params === "object" &&
          Object.keys(params).length > 0
        ) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              curl += ` --data-urlencode '${key}=${String(value)}'`;
            }
          });
        }

        if (body && config.method.toUpperCase() !== "GET") {
          if (body instanceof FormData) {
            curl += ` # FormData detected:`;
            try {
              const formEntries = [];
              for (const [key, value] of body.entries()) {
                if (value instanceof File) {
                  formEntries.push(` -F '${key}=@${value.name}'`);
                } else {
                  formEntries.push(` -F '${key}=${String(value)}'`);
                }
              }
              if (formEntries.length > 0) {
                curl += formEntries.join("");
              } else {
                curl += ` -F 'multipart-form-data'`;
              }
            } catch (e) {
              curl += ` -F 'multipart-form-data'`;
            }
          } else if (typeof body === "string") {
            curl += ` -d '${body}'`;
          } else if (typeof body === "object") {
            try {
              const jsonBody = JSON.stringify(body);
              curl += ` -d '${jsonBody}'`;
            } catch (e) {
              curl += ` -d '[Object - JSON.stringify failed]'`;
            }
          } else {
            curl += ` -d '${String(body)}'`;
          }
        }

        curl += ` --compressed --location --max-redirs 5 --retry 3`;
        return curl;
      } catch (error) {
        return `curl # Error generating command: ${error.message}`;
      }
    },
    []
  );

  const getRetryDelay = useCallback((attempt) => {
    const baseDelay = 1000;
    const maxDelay = 30000;
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 1000;
    return Math.min(maxDelay, exponentialDelay + jitter);
  }, []);

  const getErrorCategory = useCallback((error) => {
    const status = error.response?.status;

    if (!status) {
      if (error.code === "ECONNABORTED") return "TIMEOUT";
      if (error.code === "NETWORK_ERROR" || !navigator.onLine) return "NETWORK";
      return "CONNECTION";
    }

    if (status >= 500) return "SERVER_ERROR";
    if (status >= 400 && status < 500) return "CLIENT_ERROR";
    return "UNKNOWN";
  }, []);

  const sendRequest = useCallback(
    async (options) => {
      const {
        method = "GET",
        url,
        headers = {},
        params = {},
        query = {},
        body = {},
        isFormData = false,
        timeout = 300000,
        maxRetries = 3,
        priority = "normal",
        validateStatus = null,
        onStart = () => {},
        onSuccess = () => {},
        onCancel = () => {},
        onError = () => {},
        onComplete = () => {},
        onProgress = () => {},
        // onStream = () => {},
        onRetry = () => {},
        onQueueAdd = () => {},
        abortable = true,
        component = "Unknown",
        isEncrypted = true,
        enableBotStatusPolling = false,
        botStatusConfig = {
          statusUrl: API_ENDPOINTS.progressUpdateOnGrievance,
          pollInterval: 2000,
        },
        onBotStatusUpdate = () => {},
      } = options;

      const isCategorizeGrievance = url
        ?.toLowerCase()
        .includes("jb_categorize_grievance");

      const isTextLanguageDetection = url
        ?.toLowerCase()
        .includes("input_query_lang");

      const isAudioLanguageDetection = url
        ?.toLowerCase()
        .includes("inference/pipeline");

      const isVoiceFlowEnabled =
        isCategorizeGrievance ||
        isTextLanguageDetection ||
        isAudioLanguageDetection;

      const chatConfig = getChatConfig();
      // const isVoiceFlowEnabled =
      //   isCategorizeGrievance && body?.input?.isVoiceFlow === true;
      const isVoiceFlowFlagEnabled = body?.input?.isVoiceFlow === true;
      const isVoiceJourney = chatConfig?.journey === "VOICE";

      // const isVoiceFlowEnabled = Boolean(
      //   isCategorizeGrievance && isVoiceFlowFlagEnabled && isVoiceJourney
      // );

      const getEndpointName = (urlString) => {
        try {
          const urlObj = new URL(urlString, window.location.origin);
          return urlObj.pathname;
        } catch {
          return urlString;
        }
      };

      const endpointName = getEndpointName(url);
      const apiStartTime = new Date().toISOString();

      if (isGlobalAborted()) {
        logger.warning("API", "Request aborted due to global abort", {
          url,
          method,
          component,
        });

        return {
          data: null,
          error: new Error("All operations have been aborted"),
          aborted: true,
        };
      }

      const effectiveTimeout = Math.max(timeout, 300000);
      const requestId = `${method}_${url}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 11)}`;

      const abortController = abortable ? createApiController(requestId) : null;
      const requestPriority = calculateRequestPriority({
        url,
        method,
        priority,
        component,
        params,
        body,
        query,
      });
      const startTime = performance.now();

      if (!networkStatus) {
        const queuedRequest = {
          config: options,
          requestId,
          timestamp: Date.now(),
        };
        requestQueue.current.push(queuedRequest);
        onQueueAdd(queuedRequest);

        logger.warning("API", "Request queued due to offline status", {
          url,
          method,
          component,
          queueSize: requestQueue.current.length,
        });

        return {
          data: null,
          error: new Error(t("botResponseMessage.api.offlineQueued")),
          queued: true,
        };
      }

      activeRequests.current.set(requestId, {
        startTime,
        url,
        method,
        abortController,
        onCancel,
        component,
        params,
        body,
        query,
        headers,
        timeout: effectiveTimeout,
      });

      onStart({ requestId, priority: requestPriority });
      if (isVoiceFlowEnabled) {
        // startBackgroundTone();
        dispatch(setIsVoiceProcessing(true));
        dispatch(setIsVoiceMuteButtonVisible(true));
      }

      // Start bot status polling if enabled
      let botStatusPoller = null;

      if (enableBotStatusPolling && url.includes("jb_categorize_grievance")) {
        botStatusPoller = startBotStatusPolling(requestId, {
          ...botStatusConfig,
          headers: {
            ...headers,
            ...botStatusConfig.headers,
          },
          onStatusUpdate: (statusData) => {
            // console.log("Bot Status Update", statusData);

            // Generic fallback message
            const GENERIC_FALLBACK_MESSAGE =
              "Your request is being processed. Please wait...";
            const GENERIC_FALLBACK_PROGRESS = 50;
            // Resolve server message with safe fallbacks
            const serverMessage =
              statusData?.message ||
              statusData?.statusData?.message ||
              statusData?.statusData?.status ||
              GENERIC_FALLBACK_MESSAGE;

            // Resolve progress
            const progress =
              statusData?.percentage ??
              statusData?.statusData?.percentage ??
              GENERIC_FALLBACK_PROGRESS;

            onBotStatusUpdate(statusData);

            onProgress({
              requestId,
              elapsedTime: Math.round(performance.now() - startTime),
              message: serverMessage,
              type: statusData?.type || "PROCESSING",
              pollCount: statusData?.pollCount ?? 0,
              percentage: progress,
              serverData: statusData?.statusData || null,
            });
          },
        });
      }

      // const timeoutMessages = getTimeoutMessages();
      // const timeoutIds = [];

      const executeRequest = async (attemptNumber = 0) => {
        // MOVED OUTSIDE: Declare config and fullUrl outside try block for catch block access
        let config;
        let fullUrl;

        try {
          if (abortController?.signal.aborted) {
            throw new Error("Request aborted");
          }

          // Build fullUrl and config here, but they're accessible in catch
          const queryString = new URLSearchParams(query).toString();
          fullUrl = queryString ? `${url}?${queryString}` : url;

          config = {
            method,
            url: fullUrl,
            headers: {
              "X-Request-ID": requestId,
              "X-Request-Priority": requestPriority,
              ...headers,
            },
            params,
            timeout: effectiveTimeout,
            validateStatus:
              validateStatus || ((status) => status >= 200 && status < 600),
            signal: abortController?.signal,
          };

          if (isFormData && body instanceof FormData) {
            config.data = body;
            config.headers["Content-Type"] = "multipart/form-data";
          } else if (method !== "GET" && body) {
            if (isEncrypted) {
              const encryptedBody = encryptForBackend(body);
              config.data = encryptedBody;
              config.headers["X-Encrypted"] = "hybrid-rsa-aes";
            } else {
              config.data = body;
            }
            config.headers["Content-Type"] = "application/json";
          }

          // timeoutMessages.forEach(({ time, message, type }, idx) => {
          //   const id = setTimeout(() => {
          //     if (!abortController?.signal.aborted) {
          //       onProgress({
          //         requestId,
          //         elapsedTime: Math.round(performance.now() - startTime),
          //         message,
          //         step: idx + 1,
          //         totalSteps: timeoutMessages.length,
          //         type,
          //       });
          //     }
          //   }, time);
          //   timeoutIds.push(id);
          // });

          const response = await axios(config);
          // STOP BACKGROUND TONE ON SUCCESS
          if (isVoiceFlowEnabled) {
            // stopBackgroundTone();
            dispatch(setIsVoiceProcessing(false));
            dispatch(setIsVoiceMuteButtonVisible(false));
          }

          // ADD THIS: Stop bot status polling on success
          if (botStatusPoller) {
            stopBotStatusPolling(requestId);
          }
          // timeoutIds.forEach(clearTimeout);
          // Check if response status indicates an error
          if (response.status >= 400) {
            let serverError = new Error(
              `Request failed with status code ${response.status}`
            );
            serverError.code =
              response.status >= 500 ? "ERR_SERVER_ERROR" : "ERR_CLIENT_ERROR";
            if (response.status === 400 && url.includes("lodge")) {
              serverError = new Error(response.data.message);
              serverError.code = "ERR_NETWORK";
            }
            serverError.response = response;
            serverError.request = response.request;
            serverError.config = response.config;

            throw serverError;
          }

          const metrics = {
            requestId,
            duration: Math.round(performance.now() - startTime),
            status: response.status,
            attempts: attemptNumber + 1,
          };

          const apiEndTime = new Date().toISOString();
          const calculatedTime = metrics.duration;
          // NEW: Prepare log data to send to server
          const logData = {
            endpoint: endpointName,
            method,
            url,
            startTime: apiStartTime,
            endTime: apiEndTime,
            calculatedTime: `${calculatedTime}`,
            status: response.status,
            attempts: metrics.attempts,
            payload: method !== "GET" ? body : params,
            query,
            component,
          };

          const xSource =
            headers["x-source"] ||
            headers["X-Source"] ||
            config.headers["x-source"] ||
            config.headers["X-Source"] ||
            selectedChatId?.replace(/-/g, "_") ||
            "unknown";

          let loggerPayload = {
            user_id_nic: "temp",
            turn_id: "temp",
            service_type: "Frontend",
            service_name: `${endpointName}`,
            time_taken_seconds: calculatedTime,
          };

          // Log locally
          logger.info(
            "API_REQUEST_SUCCESS",
            `${method} ${endpointName} - ${calculatedTime}ms`,
            logData
          );

          // NEW: Send log to server (non-blocking)
          sendLogToServer(loggerPayload, xSource); // data is being sent to backend server

          onProgress({
            requestId,
            elapsedTime: metrics.duration,
            message: t("botResponseMessage.api.requestComplete"),
            // step: timeoutMessages.length + 1,
            // totalSteps: timeoutMessages.length + 1,
          });

          onSuccess(response.data, metrics);
          activeRequests.current.delete(requestId);
          retryAttempts.current.delete(requestId);

          return { data: response.data, error: null, metrics };
        } catch (err) {
          // STOP BACKGROUND TONE ON ERROR
          if (isVoiceFlowEnabled) {
            // stopBackgroundTone();
            dispatch(setIsVoiceProcessing(false));
            dispatch(setIsVoiceMuteButtonVisible(false));
          }

          // timeoutIds.forEach(clearTimeout);
          // ADD THIS: Stop bot status polling on error
          if (botStatusPoller) {
            stopBotStatusPolling(requestId);
          }

          // Now config and fullUrl are accessible here
          // logger.info("API", "Request error encountered", {
          //   requestId,
          //   url: fullUrl || url, // fallback to original url if fullUrl not set
          //   method,
          //   error: err,
          //   attempt: attemptNumber + 1,
          //   maxRetries,
          //   component,
          // });

          const errorCategory = getErrorCategory(err);
          const errorMetrics = {
            requestId,
            duration: Math.round(performance.now() - startTime),
            attempts: attemptNumber + 1,
            status: err.response?.status || 0,
          };

          // Enhanced error tracking data with proper config and fullUrl access
          const errorTrackingData = {
            message: `${errorCategory}: ${err.message}`,
            code: err.code || "UNKNOWN_ERROR",
            status: err.response?.status || 0,
            statusText: err.response?.statusText || "Unknown Status",
            responseData: err.response?.data,
            responseHeaders: err.response?.headers,
            url: fullUrl || url, // Use fullUrl if available, fallback to original url
            method,
            requestId,
            duration: errorMetrics.duration,
            attempts: errorMetrics.attempts,
            timeout: effectiveTimeout,
            curl: generateCurlCommand(
              config || { method, url: fullUrl || url, headers },
              body,
              params,
              query
            ),
            headers: config?.headers || headers,
            requestData: config?.data || body,
            component,
            stack: err.stack,
            maxRetries,
            shouldRetry: shouldRetry(err, attemptNumber, { maxRetries }),
            errorCategory,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            networkStatus,
          };

          // Handle abort
          if (err.name === "AbortError" || err.message === "Request aborted") {
            if (isVoiceFlowEnabled) {
              // stopBackgroundTone();
              dispatch(setIsVoiceProcessing(false));
              dispatch(setIsVoiceMuteButtonVisible(false));
            }

            const abortMetrics = {
              requestId,
              duration: Math.round(performance.now() - startTime),
              attempts: attemptNumber + 1,
              status: 0,
            };

            logger.info("API", "Request aborted", {
              requestId,
              url: fullUrl || url,
              method,
              duration: abortMetrics.duration,
              component,
            });

            onCancel({
              requestId,
              reason: "Request aborted",
              metrics: abortMetrics,
            });

            activeRequests.current.delete(requestId);
            retryAttempts.current.delete(requestId);

            return {
              data: null,
              error: new Error("Request aborted"),
              metrics: abortMetrics,
              aborted: true,
              requestId,
            };
          }

          // Handle retries
          if (shouldRetry(err, attemptNumber, { maxRetries })) {
            const retryDelay = getRetryDelay(attemptNumber);

            // Track retry attempt
            await trackApiError({
              ...errorTrackingData,
              message: `${errorCategory}: Retrying (attempt ${
                attemptNumber + 1
              }/${maxRetries}) - ${err.message}`,
              retryDelay,
              isRetryAttempt: true,
            });

            onRetry({
              error: err,
              attempt: attemptNumber + 1,
              maxRetries,
              retryDelay,
              requestId,
            });

            return new Promise((resolve) => {
              const retryTimeout = setTimeout(async () => {
                if (abortController?.signal.aborted) {
                  resolve({
                    data: null,
                    error: new Error("Request aborted during retry"),
                    aborted: true,
                    requestId,
                  });
                } else {
                  resolve(await executeRequest(attemptNumber + 1));
                }
              }, retryDelay);

              if (abortController?.signal) {
                abortController.signal.addEventListener("abort", () => {
                  clearTimeout(retryTimeout);
                  resolve({
                    data: null,
                    error: new Error("Request aborted during retry"),
                    aborted: true,
                    requestId,
                  });
                });
              }
            });
          }

          // Final failure - track based on error type
          if (err.code === "ECONNABORTED") {
            await trackTimeoutError({
              url: fullUrl || url,
              method,
              requestId,
              duration: errorMetrics.duration,
              attempts: errorMetrics.attempts,
              timeout: effectiveTimeout,
              curl: generateCurlCommand(
                config || { method, url: fullUrl || url, headers },
                body,
                params,
                query
              ),
              headers: config?.headers || headers,
              requestData: config?.data || body,
              component,
            });
          } else if (errorCategory === "SERVER_ERROR") {
            let serverErrorType = "OTHER_SERVER_ERROR";

            switch (err.response?.status) {
              case 500:
                serverErrorType = "INTERNAL_SERVER_ERROR";
                break;
              case 502:
                serverErrorType = "BAD_GATEWAY";
                break;
              case 503:
                serverErrorType = "SERVICE_UNAVAILABLE";
                break;
              case 504:
                serverErrorType = "GATEWAY_TIMEOUT";
                break;
            }

            await trackApiError({
              ...errorTrackingData,
              message: `SERVER ERROR (${err.response?.status}): ${err.message}`,
              serverErrorType,
              isFinalFailure: true,
            });
          } else if (errorCategory === "CLIENT_ERROR") {
            let clientErrorType = "OTHER_CLIENT_ERROR";

            switch (err.response?.status) {
              case 400:
                clientErrorType = "BAD_REQUEST";
                break;
              case 401:
                clientErrorType = "UNAUTHORIZED";
                break;
              case 403:
                clientErrorType = "FORBIDDEN";
                break;
              case 404:
                clientErrorType = "NOT_FOUND";
                break;
              case 429:
                clientErrorType = "TOO_MANY_REQUESTS";
                break;
            }

            await trackApiError({
              ...errorTrackingData,
              message: `CLIENT ERROR (${err.response?.status}): ${err.message}`,
              clientErrorType,
              isFinalFailure: true,
            });
          } else {
            // Track network/connection errors
            await trackNetworkError(err, {
              url: fullUrl || url,
              method,
              requestId,
              duration: errorMetrics.duration,
              attempts: errorMetrics.attempts,
              timeout: effectiveTimeout,
              curl: generateCurlCommand(
                config || { method, url: fullUrl || url, headers },
                body,
                params,
                query
              ),
              headers: config?.headers || headers,
              requestData: config?.data || body,
              component,
              maxRetries,
            });
          }

          let errorMessage;

          if (err.code === "ECONNABORTED") {
            errorMessage = t("botResponseMessage.api.requestTimeout");
          } else if (errorCategory === "SERVER_ERROR") {
            errorMessage = t("botResponseMessage.api.serverError");
          } else if (errorCategory === "ERR_NETWORK") {
            errorMessage = t("botResponseMessage.api.serverError");
          } else {
            // console.log("Error:", err);
            errorMessage = t("botResponseMessage.api.requestError");
          }

          onProgress({
            requestId,
            elapsedTime: errorMetrics.duration,
            message: errorMessage,
          });

          onError(err, errorMetrics);
          activeRequests.current.delete(requestId);
          retryAttempts.current.delete(requestId);

          return { data: null, error: err, metrics: errorMetrics };
        }
      };

      try {
        return await executeRequest(0);
      } finally {
        // SAFETY: ensure tone is always stopped
        if (isVoiceFlowEnabled) {
          // stopBackgroundTone();
          dispatch(setIsVoiceProcessing(false));
          dispatch(setIsVoiceMuteButtonVisible(false));
        }

        // ADD THIS: Stop bot status polling on error
        if (botStatusPoller) {
          stopBotStatusPolling(requestId);
        }

        if (url.includes("jb_categorize_grievance")) {
          dispatch(setInputDisabled(false));
          dispatch(setSendButtonDisabled(false));
          dispatch(setMicButtonDisabled(false));

          logger.info(
            "API",
            "Re-enabled input controls after categorize grievance",
            {
              requestId,
              url,
              component,
            }
          );
        }
        // timeoutIds.forEach(clearTimeout);
        onComplete({ requestId });
      }
    },
    [
      t,
      networkStatus,
      shouldRetry,
      getRetryDelay,
      calculateRequestPriority,
      // getTimeoutMessages,
      createApiController,
      isGlobalAborted,
      generateCurlCommand,
      getErrorCategory,
      startBotStatusPolling, // ADD THIS
      stopBotStatusPolling, // ADD THIS
      sendLogToServer, // ADD THIS
    ]
  );

  // Rest of your methods remain unchanged...
  const cancelRequest = useCallback(
    (requestId) => {
      const request = activeRequests.current.get(requestId);
      if (request) {
        if (request.url?.toLowerCase().includes("jb_categorize_grievance")) {
          // stopBackgroundTone();
          dispatch(setIsVoiceProcessing(false));
        }

        logger.info("API", "Cancelling individual request", {
          requestId,
          url: request.url,
          method: request.method,
          component: request.component,
          duration: Math.round(performance.now() - request.startTime),
        });

        if (request.onCancel) {
          request.onCancel({
            requestId,
            reason: "Request cancelled manually",
            url: request.url,
            method: request.method,
          });
        }
      }

      return abortApiRequest(requestId);
    },
    [abortApiRequest]
  );

  const cancelAllRequests = useCallback(() => {
    try {
      const activeCount = activeRequests.current.size;
      const queuedCount = requestQueue.current.length;

      logger.info("API", "Cancelling all requests", {
        activeCount,
        queuedCount,
        totalCancelled: activeCount + queuedCount,
      });

      const activeRequestsDetails = Array.from(
        activeRequests.current.entries()
      ).map(([requestId, req]) => ({
        requestId,
        url: req.url,
        method: req.method,
        component: req.component,
        duration: Math.round(performance.now() - req.startTime),
        timeout: req.timeout,
        curl: generateCurlCommand(
          {
            method: req.method,
            url: req.url,
            headers: req.headers,
          },
          req.body,
          req.params,
          req.query
        ),
      }));

      const queuedRequestsDetails = requestQueue.current.map((req) => ({
        url: req.config.url,
        method: req.config.method,
        component: req.config.component,
        queuedAt: req.timestamp,
        queueDuration: Date.now() - req.timestamp,
        curl: generateCurlCommand(
          {
            method: req.config.method,
            url: req.config.url,
            headers: req.config.headers,
          },
          req.config.body,
          req.config.params,
          req.config.query
        ),
      }));

      logger.info("API", "Active requests details", {
        requests: activeRequestsDetails,
      });

      let curl = "# No active or queued requests to cancel";

      if (activeCount > 0) {
        const activeSection = `# Active requests cancelled (${activeCount}):\n${activeRequestsDetails
          .map((req) => req.curl)
          .join("\n")}\n`;

        const queuedSection =
          queuedCount > 0
            ? `# Queued requests cancelled (${queuedCount}):\n${queuedRequestsDetails
                .map((req) => req.curl)
                .join("\n")}`
            : "";

        curl = activeSection + queuedSection;
      }

      trackApiError({
        message: "All API requests cancelled",
        code: "ALL_REQUESTS_CANCELLED",
        status: 0,
        url:
          activeCount > 0
            ? activeRequestsDetails.map((req) => req.url).join(", ")
            : null,
        method:
          activeCount > 0
            ? activeRequestsDetails.map((req) => req.method).join(", ")
            : null,
        requestId:
          Array.from(activeRequests.current.keys()).join(", ") || "BULK_CANCEL",
        duration:
          activeCount > 0
            ? Math.max(...activeRequestsDetails.map((req) => req.duration))
            : 0,
        attempts: 0,
        timeout:
          activeCount > 0
            ? Math.max(...activeRequestsDetails.map((req) => req.timeout || 0))
            : 0,
        curl: curl,
        headers: null,
        requestData: null,
        component: "useApi - Global Cancel",
        stack: new Error().stack,
        maxRetries: 0,
        shouldRetry: false,
        bulkOperation: true,
        activeRequestsCount: activeCount,
        queuedRequestsCount: queuedCount,
        totalAffectedRequests: activeCount + queuedCount,
        cancelledRequests: activeRequestsDetails,
        queuedRequests: queuedRequestsDetails,
        networkStatus,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        performanceMetrics: {
          memoryUsage: performance.memory
            ? {
                usedJSHeapSize: performance.memory.usedJSHeapSize,
                totalJSHeapSize: performance.memory.totalJSHeapSize,
                jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
              }
            : null,
          connectionType: navigator.connection
            ? navigator.connection.effectiveType
            : "unknown",
        },
      });

      const requestsToCancel = Array.from(activeRequests.current.entries());
      requestsToCancel.forEach(([requestId, requestInfo]) => {
        if (requestInfo.onCancel) {
          requestInfo.onCancel({
            requestId,
            reason: "All requests cancelled",
            url: requestInfo.url,
            method: requestInfo.method,
          });
        }
      });

      const abortedCount = abortAllApiRequests();
      activeRequests.current.clear();
      retryAttempts.current.clear();
      requestQueue.current = [];

      logger.info("API", "All requests cancelled successfully", {
        abortedCount,
      });

      return abortedCount;
    } catch (error) {
      logger.error("API", "Error cancelling all requests", {
        error: error.message,
        activeRequests: activeRequests.current.size,
        queuedRequests: requestQueue.current.length,
      });

      activeRequests.current.clear();
      retryAttempts.current.clear();
      requestQueue.current = [];

      return 0;
    }
  }, [abortAllApiRequests, generateCurlCommand, networkStatus]);

  const processQueuedRequests = useCallback(async () => {
    const queue = [...requestQueue.current];
    requestQueue.current = [];

    if (queue.length > 0) {
      logger.info("API", "Processing queued requests", {
        queueSize: queue.length,
      });
    }

    let successCount = 0;
    let errorCount = 0;

    for (const queuedRequest of queue) {
      try {
        await sendRequest(queuedRequest.config);
        successCount++;
        logger.debug("API", "Queued request processed successfully", {
          url: queuedRequest.config.url,
          method: queuedRequest.config.method,
        });
      } catch (error) {
        errorCount++;
        logger.error("API", "Failed to process queued request", {
          url: queuedRequest.config.url,
          method: queuedRequest.config.method,
          error: error.message,
          queuedAt: queuedRequest.timestamp,
          processedAt: Date.now(),
        });
      }
    }

    if (queue.length > 0) {
      logger.info("API", "Queue processing completed", {
        totalProcessed: queue.length,
        successCount,
        errorCount,
        successRate: (successCount / queue.length) * 100,
      });
    }
  }, [sendRequest]);

  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus(true);
      logger.info(
        "NETWORK",
        "Connection restored - processing queued requests",
        {
          queuedRequestsCount: requestQueue.current.length,
          activeRequestsCount: activeRequests.current.size,
        }
      );
      processQueuedRequests();
    };

    const handleOffline = () => {
      setNetworkStatus(false);
      logger.warning("NETWORK", "Connection lost - queuing future requests", {
        activeRequestsCount: activeRequests.current.size,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [processQueuedRequests]);

  return {
    sendRequest,
    cancelRequest,
    cancelAllRequests,
    getActiveRequestsCount: () => activeRequests.current.size,
    getQueuedRequestsCount: () => requestQueue.current.length,
    getApiMetrics: () => ({
      activeRequests: activeRequests.current.size,
      queuedRequests: requestQueue.current.length,
      networkStatus,
    }),
    networkStatus,
    processQueuedRequests,
  };
};
